// SP3c — ComposerEditor: the unified, tabbed composer editor (route
// /composers/:id). It wraps the EXISTING detail + layout editors as tab panels
// inside the save registry + shell, turning the five separate save buttons into
// one "Enregistrer la composition" and removing the detail↔layout page-hop.
//
// DS §5: the editors keep their own state + mutation calls (payloads unchanged);
// `embedded` only hides their chrome + registers their save. ComposerEditor adds
// no payload logic — it just composes provider + shell + the three panels.
// (The rename PATCH below is composer METADATA, not a program/layout payload —
// it is the same `updateComposer` call the detail page's header used to own.)
import { useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  getCapabilityMatrix,
  getComposer,
  updateComposer,
  type ComposerDetail,
} from '@/api/composers';
import type { ApiError } from '@/api/client';
import type { ResourceState } from '@displaycms/contracts';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Icon } from '@/components/icon';
import { pickName } from '@/lib/i18n-name';
import { STATE_BADGE_VARIANT } from '@/lib/state-badge';
import { useAuth } from '@/auth/auth-context';
import { ComposerSaveProvider } from './composer-save-registry';
import { ComposerEditorShell } from './composer-editor-shell';
import { ComposerDetailPage, AuditDrawer } from './detail';
import { ComposerLayoutEditorPage } from './layout-editor';
import { ClassMismatchChip } from './design-class';

export function ComposerEditor(): JSX.Element {
  const params = useParams<{ id: string }>();
  const composerId = params.id ?? '';
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const { t, i18n } = useTranslation(['composers', 'common']);
  const lang = i18n.language.startsWith('en') ? 'en' : 'fr';
  const canEdit = hasPermission('composers.update');
  const initialTab = searchParams.get('tab') === 'layout' ? 'layout' : 'content';

  // Only needed for the Approbation tab (AuditDrawer wants the current state);
  // the key is shared with both editors' queries, so this dedupes — no extra
  // network round-trip.
  const detailQuery = useQuery<ComposerDetail>({
    queryKey: ['composers', 'detail', composerId],
    queryFn: () => getComposer(composerId),
    enabled: composerId.length > 0,
    staleTime: 30_000,
  });

  // Capability matrix for the hero's class-mismatch chip. SAME queryKey as
  // the embedded detail page's matrixQuery, so react-query dedupes — no
  // extra network round-trip.
  const matrixQuery = useQuery({
    queryKey: ['composers', 'capability-matrix', composerId],
    queryFn: () => getCapabilityMatrix(composerId),
    enabled: composerId.length > 0,
    staleTime: 30_000,
  });

  // Rename affordance for the hero. Clicking `composer-rename-btn` swaps the
  // H1 for an Input + Save/Cancel (moved here from the detail page's
  // standalone header, which no production route renders since SP3c).
  const [renameMode, setRenameMode] = useState(false);
  const [renameFrDraft, setRenameFrDraft] = useState('');
  const [renameEnDraft, setRenameEnDraft] = useState('');
  const renameMutation = useMutation<unknown, ApiError, { fr: string; en?: string }>({
    mutationFn: async (next) =>
      updateComposer(composerId, {
        name_i18n: next.en ? { fr: next.fr, en: next.en } : { fr: next.fr },
      }),
    onSuccess: () => {
      setRenameMode(false);
      void queryClient.invalidateQueries({
        queryKey: ['composers', 'detail', composerId],
      });
      void queryClient.invalidateQueries({ queryKey: ['composers', 'list'] });
    },
  });

  const detail = detailQuery.data;
  const versionText = detail
    ? detail.current_version_id === null
      ? t('composers:detail.version_unsaved')
      : t('composers:detail.version_label', {
          version:
            typeof detail.current_version_number === 'number'
              ? detail.current_version_number
              : '—',
        })
    : null;

  return (
    <ComposerSaveProvider>
      {/* ---------------------------------------------------------------- *
       * HERO (SP4) — 2026 detail-page visual language adapted for an
       * editor: composition (layers) icon + name (with rename) + id, with a
       * right-aligned state badge + version label + class-mismatch chip.
       * The unified save + per-tab dirty dots live in the shell directly
       * below; nothing here touches a program/layout payload (the rename is
       * a metadata PATCH).
       * ---------------------------------------------------------------- */}
      <header className="mb-4 flex flex-col gap-4" data-testid="composer-editor-header">
        <Link
          to="/composers"
          className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:underline"
          data-testid="composer-editor-back"
        >
          <Icon name="chevron-left" size={16} aria-hidden />
          {t('composers:editor.back', { defaultValue: 'Retour aux compositions' })}
        </Link>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg border border-border bg-muted text-muted-foreground">
              <Icon name="layers" size={28} aria-hidden />
            </span>
            <div className="flex flex-col gap-1">
              {renameMode && detail ? (
                <div
                  className="flex items-center gap-2"
                  data-testid="composer-rename-form"
                >
                  <Input
                    value={renameFrDraft}
                    onChange={(e) => setRenameFrDraft(e.currentTarget.value)}
                    placeholder="Nom FR"
                    data-testid="composer-rename-fr"
                    className="w-48"
                  />
                  <Input
                    value={renameEnDraft}
                    onChange={(e) => setRenameEnDraft(e.currentTarget.value)}
                    placeholder="Nom EN (optionnel)"
                    data-testid="composer-rename-en"
                    className="w-48"
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      if (renameFrDraft.trim().length === 0) return;
                      renameMutation.mutate(
                        renameEnDraft.trim().length > 0
                          ? { fr: renameFrDraft.trim(), en: renameEnDraft.trim() }
                          : { fr: renameFrDraft.trim() },
                      );
                    }}
                    disabled={
                      renameMutation.isPending ||
                      renameFrDraft.trim().length === 0
                    }
                    data-testid="composer-rename-save"
                  >
                    {t('common:save', { defaultValue: 'Enregistrer' })}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setRenameMode(false)}
                    disabled={renameMutation.isPending}
                    data-testid="composer-rename-cancel"
                  >
                    {t('common:cancel', { defaultValue: 'Annuler' })}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-semibold tracking-tight">
                    {detail ? pickName(detail.name_i18n, lang) : '…'}
                  </h1>
                  {detail && canEdit && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setRenameFrDraft(detail.name_i18n.fr);
                        setRenameEnDraft(detail.name_i18n.en ?? '');
                        setRenameMode(true);
                      }}
                      data-testid="composer-rename-btn"
                    >
                      {t('common:rename', { defaultValue: 'Renommer' })}
                    </Button>
                  )}
                </div>
              )}
              {detail && (
                <code className="font-mono text-xs text-muted-foreground">
                  {detail.id}
                </code>
              )}
            </div>
          </div>

          {detail && (
            <div className="flex flex-wrap items-center gap-2 sm:justify-end">
              <Badge variant={STATE_BADGE_VARIANT[detail.state as ResourceState]} data-testid="composer-editor-state">
                {t(`composers:state.${detail.state}`, { defaultValue: detail.state })}
              </Badge>
              <span
                className="text-sm text-muted-foreground"
                data-testid="composer-version"
              >
                {versionText}
              </span>
              <ClassMismatchChip matrix={matrixQuery.data} lang={lang} />
            </div>
          )}
        </div>
      </header>
      <ComposerEditorShell
        initialTab={initialTab}
        canEdit={canEdit}
        contentPanel={<ComposerDetailPage embedded />}
        layoutPanel={<ComposerLayoutEditorPage embedded />}
        auditPanel={
          detailQuery.data ? (
            <AuditDrawer
              composerId={composerId}
              state={detailQuery.data.state as ResourceState}
            />
          ) : null
        }
      />
    </ComposerSaveProvider>
  );
}

export default ComposerEditor;

// SP3c: the old /composers/:id/layout route redirects into the tabbed editor
// with the Mise-en-page tab active (preserves any deep links).
export function ComposerLayoutRedirect(): JSX.Element {
  const params = useParams<{ id: string }>();
  return <Navigate to={`/composers/${params.id ?? ''}?tab=layout`} replace />;
}
